import { useAuth } from '@/auth/Auth.tsx';
import Loader from '@/components/Loader/Loader.tsx';
import CardCurrency from '@/components/shared/CardCurrency/CardCurrency.tsx';
import LayoutInside from '@/layout/LayoutInside';
import MetricsCard from '@/pages/Dashboard/Partials/MetricsCard.tsx';
import NextRewardCard from '@/pages/Dashboard/Partials/NextRewardCard.tsx';
import SalesByCountryCard from '@/pages/Dashboard/Partials/SalesByCountryCard.tsx';
import { usePermissions } from '@/auth/PermissionsContext';
import { differenceInDays, format, isSameDay, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TFunction } from 'i18next';
import { useEffect, useMemo } from 'react';
import { Cookies } from 'react-cookie';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useMetrics } from './hooks/useMetrics';
import DateRangePicker from './Partials/DateRangePicker.tsx';
import HourlySalesChart from './Partials/HourlySalesChart';
import NewsBanner from './Partials/NewsBanner.tsx';
import RevisionAlertBanner from './Partials/RevisionAlertBanner.tsx';
import { useNews } from './hooks/useNews';
import { usePendingRevisions } from './hooks/usePendingRevisions';
import { RetentionResponse } from '@/services/wallet.service.ts';

const getSalesPeriodText = (
  range: DateRange | undefined,
  t: TFunction<'dashboard', undefined>
): string => {
  if (!range?.from) return t('sales');

  if (range.to && isSameDay(range.from, range.to)) {
    return isToday(range.from)
      ? t('salesToday')
      : t('salesOnDate', {
          date: format(range.from, 'dd/MM/yyyy', { locale: ptBR }),
        });
  }

  if (range.to) {
    const days = differenceInDays(range.to, range.from);

    const predefinedRanges = {
      6: t('last7Days'),
      29: t('last30Days'),
      89: t('last90Days'),
    };

    if (days in predefinedRanges) {
      return predefinedRanges[days as keyof typeof predefinedRanges];
    }

    return t('salesRange', {
      from: format(range.from, 'dd/MM', { locale: ptBR }),
      to: format(range.to, 'dd/MM', { locale: ptBR }),
    });
  }

  return t('sales');
};

export default function Dashboard() {
  const { t } = useTranslation('dashboard');
  const { authData } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissions();

  const canViewSaleData  = hasPermission('metrics.view_sale_data');
  const canViewAvailable = hasPermission('wallet.view_available_balance');
  const canViewToRelease = hasPermission('wallet.view_balance_to_release');
  const canViewMetrics   = hasPermission('metrics.view_dashboard_metrics');
  const canViewReward    = hasPermission('wallet.view_total_withdrawn');
  const canViewRetention = hasAnyPermission(['wallet.view_available_balance', 'wallet.view_balance_to_release']);
  const canViewProducts  = hasAnyPermission(['products.view_products', 'products.edit_product']);

  const hasDashboardAccess = canViewSaleData || canViewAvailable || canViewToRelease || canViewMetrics || canViewReward;

  const {
    metrics,
    salesByCountry,
    reports,
    paidSales,
    retention,
    handleDateRangeChange,
    dateRange,
    nextReward,
    isLoading,
    currency,
    totalAmount,
  } = useMetrics();

  const { news } = useNews();
  const { pendingRevisions } = usePendingRevisions();

  useEffect(() => {
    const cookies = new Cookies();
    if (cookies.get('mundpay_login_by_imp') !== 'false') {
      cookies.set('mundpay_login_by_imp', 'false', {
        path: '/',
        maxAge: 60 * 60,
      });
    }
  }, []);

  const breadcrumbItems = useMemo(
    () => [
      {
        text: t('dashboard'),
        link: '/dashboard',
      },
    ],
    [t]
  );

  const salesPeriodText = useMemo(
    () => getSalesPeriodText(dateRange, t),
    [dateRange, t]
  );

  const defaultCurrencyFallback = useMemo(() => {
    return currency === 'USD' ? 'US$ 0.00' : 'R$ 0,00';
  }, [currency]);

  const getFirstName = (fullName?: string) => {
    return fullName?.split(' ')[0] || t('defaultUser');
  };

  const firstName = getFirstName(authData?.name);

  if (!authData) {
    return (
      <LayoutInside items={breadcrumbItems}>
        <Loader fullScreen />
      </LayoutInside>
    );
  }


  return (
    <LayoutInside hasBgBlur items={breadcrumbItems}>
      <div className="space-y-6">
        <header className="flex gap-4 items-center justify-between flex-wrap">
          <h1 className="text-2xl font-bold">
            {t("greeting", { name: firstName })}
          </h1>
          {canViewSaleData && (
            <div className='flex items-center gap-2'>
              <DateRangePicker
                defaultValue={dateRange}
                onChange={handleDateRangeChange}
              />
            </div>
          )}
        </header>

        {canViewProducts && pendingRevisions.length > 0 && (
          <RevisionAlertBanner products={pendingRevisions} />
        )}

        {news.length > 0 && <NewsBanner news={news} />}

        {!hasDashboardAccess && (
          <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
            <p className="text-lg font-medium">{t('no_permission_title', 'Sem permissão')}</p>
            <p className="text-sm mt-1">{t('no_permission_description', 'Você não tem permissão para visualizar os dados desta tela.')}</p>
          </div>
        )}

        {hasDashboardAccess && (
          <>
            {(canViewSaleData || canViewAvailable || canViewToRelease) && (
              <section className='grid grid-cols-1 gap-4 md:grid-cols-3'>
                {canViewSaleData && (
                  <CardCurrency
                    text={salesPeriodText}
                    value={
                      paidSales?.data?.paid?.totals?.amount?.total ?? defaultCurrencyFallback
                    }
                    status='period'
                    isLoading={isLoading}
                  />
                )}
                {canViewAvailable && (
                  <CardCurrency
                    text={t('available')}
                    value={
                      (retention as RetentionResponse).items?.find(
                        (item) => item.type === 'available'
                      )?.total_formatted ?? defaultCurrencyFallback
                    }
                    status='paid'
                  />
                )}
                {canViewToRelease && (
                  <CardCurrency
                    text={t('toRelease')}
                    value={
                      (retention as RetentionResponse).items?.find(
                        (item) => item.type === 'receivable'
                      )?.total_formatted ?? defaultCurrencyFallback
                    }
                    status='refused'
                  />
                )}
              </section>
            )}

            {(canViewSaleData || canViewReward) && (
              <section className='flex flex-col md:flex-row gap-4'>
                {canViewSaleData && (
                  <HourlySalesChart
                    text={salesPeriodText}
                    isLoadingData={isLoading}
                    hourlySales={reports}
                  />
                )}
                {canViewReward && (
                  <NextRewardCard
                    nextReward={nextReward}
                    totalAmount={totalAmount}
                    currency={currency}
                  />
                )}
              </section>
            )}

            {(canViewSaleData || canViewMetrics) && (
              <section className='grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch'>
                {canViewSaleData && (
                  <SalesByCountryCard salesByCountry={salesByCountry} isLoading={isLoading} />
                )}
                {canViewSaleData && canViewMetrics && (
                  <MetricsCard isLoadingData={isLoading} metrics={metrics?.data} />
                )}
              </section>
            )}
          </>
        )}
      </div>
    </LayoutInside>
  );
}
